class CollectionGridManager {
  constructor() {
    // Collection heading elements
    this.catSwitcherMenu = document.querySelector(".CAT-header__nav-list");
    this.catSwitcherMenuTabs = this.catSwitcherMenu?.querySelectorAll(".CAT-header__nav-link");
    this.customTabs = this.catSwitcherMenu?.closest('.custom-tabs');

    // Infinite scroll properties
    this.pageNr = 1;
    this.CAT_collection_grid_template_id = document.querySelector('#product-grid')?.dataset.id;
    this.isFetching = false;
    this.UTMproductsRearenged = false;

    this.init();
  }

  init() {
    this.triggerSearchPopup();
    this.initInfiniteScroll();
    this.setupInitialFilters();
    this.handleStickyFilterButton();
    this.updateBodyCollectionClass(this.getCurrentCollectionHandleFromPath());
    this.initDossierOriginals();
    this.collectionSwitcherMenuHandler();
    this.initPromoCardsVideoLogic();
  }

  initPromoCardsVideoLogic() {
    const videos = document.querySelectorAll('.promo-card .background-video[data-desktop-src][data-mobile-src]');
    if (!videos.length) return;

    const updatePauseIcon = (video) => {
      const mediaContainer = video.closest('.promo-card--media');
      if (!mediaContainer) return;

      const pauseIconOn = mediaContainer.querySelector('.promo-card-video-icon--pause');
      const pauseIconOff = mediaContainer.querySelector('.promo-card-video-icon--play');
      if (!pauseIconOn || !pauseIconOff) return;

      pauseIconOn.classList.toggle('hidden', video.paused);
      pauseIconOff.classList.toggle('hidden', !video.paused);
    };

    const playVideo = (video) => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    // Points the video at the current breakpoint's source; returns true when the source changed
    const loadVideoSource = (video) => {
      const source = video.querySelector('source');
      if (!source) return false;

      const isDesktop = window.innerWidth > 991;
      const nextSrc = isDesktop ? video.dataset.desktopSrc : video.dataset.mobileSrc;
      if (!nextSrc || source.getAttribute('src') === nextSrc) return false;

      source.src = nextSrc;
      video.load();
      return true;
    };

    // Load and play promo videos only near the viewport and pause them once they leave it. Loading and
    // playing every promo video up front wastes bandwidth during page load (LCP) and keeps the compositor
    // busy decoding off-screen video, which delays the next paint after interactions (INP).
    this.promoCardsVideoObserver?.disconnect();
    this.promoCardsVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ target: video, isIntersecting }) => {
        if (!isIntersecting) {
          video.pause();
          return;
        }

        loadVideoSource(video);
        if (video.autoplay && video.dataset.userPaused !== 'true') playVideo(video);
      });
    }, { rootMargin: '200px 0px' });

    videos.forEach((video) => {
      const mediaContainer = video.closest('.promo-card--media');
      if (!mediaContainer) return;

      const pauseButton = mediaContainer.querySelector('.promo-card-video-toggle');
      if (pauseButton && pauseButton.dataset.bound !== 'true') {
        pauseButton.dataset.bound = 'true';
        pauseButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();

          // Remember the shopper's choice so scrolling back into view doesn't override it
          if (video.paused) {
            video.dataset.userPaused = 'false';
            playVideo(video);
          } else {
            video.dataset.userPaused = 'true';
            video.pause();
          }
        }, true);

        video.addEventListener('play', () => updatePauseIcon(video));
        video.addEventListener('pause', () => updatePauseIcon(video));
        video.addEventListener('loadeddata', () => updatePauseIcon(video));
      }

      // On resize, swap the source of videos that were already loaded if the breakpoint changed
      if (video.querySelector('source')?.getAttribute('src')) {
        const wasPlaying = !video.paused;
        if (loadVideoSource(video) && wasPlaying) playVideo(video);
      }

      this.promoCardsVideoObserver.observe(video);
      updatePauseIcon(video);
    });

    if (!this.promoCardsVideoResizeBound) {
      this.promoCardsVideoResizeBound = true;
      window.addEventListener('resize', () => this.initPromoCardsVideoLogic());
    }
  }

  getCurrentCollectionHandleFromPath() {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const collectionIndex = pathParts.indexOf('collections');
    if (collectionIndex === -1 || !pathParts[collectionIndex + 1]) return '';
    return pathParts[collectionIndex + 1];
  }

  updateBodyCollectionClass(handle) {
    if (!handle || !document.body) return;

    const existingCollectionClasses = Array.from(document.body.classList).filter((className) => className.startsWith('collection__'));
    existingCollectionClasses.forEach((className) => document.body.classList.remove(className));

    document.body.classList.add(`collection__${handle}`);
  }

  initDossierOriginals() {
    if (this.getCurrentCollectionHandleFromPath() !== 'dossier-originals') return;

    this.initDossierOriginalsVideo();
    this.initDossierOriginalsPromoBox();
  }

  initDossierOriginalsVideo() {
    document.querySelectorAll('.dossier-originals_container_body .collection-showcase_video').forEach((container) => {
      const video = container.querySelector('video');
      const source = video?.querySelector('source');
      const muteButton = container.querySelector('.mute-icon_for_video');
      const pauseButton = container.querySelector('.pause-icon_for_video');
      if (!video) return;

      const playVideo = () => {
        video.play().catch((error) => console.error('Error playing Originals video:', error));
      };

      const updateVideoSource = () => {
        const isDesktop = window.innerWidth > 768;
        const videoUrl = isDesktop ? container.dataset.videoDesktop : container.dataset.videoMobile;
        const posterUrl = isDesktop ? container.dataset.posterDesktop : container.dataset.posterMobile;
        if (!source || !videoUrl || source.getAttribute('src') === videoUrl) return;

        source.src = videoUrl;
        if (posterUrl) video.poster = posterUrl;
        video.load();
        if (video.autoplay) playVideo();
      };

      updateVideoSource();
      if (container.dataset.videoLogicInitialized === 'true') return;
      container.dataset.videoLogicInitialized = 'true';

      const updateMuteIcon = () => {
        muteButton?.querySelector('.mute-icon--muted, .mute-icon-on')?.classList.toggle('hidden', !video.muted);
        muteButton?.querySelector('.mute-icon--unmuted, .mute-icon-off')?.classList.toggle('hidden', video.muted);
      };

      const updatePauseIcon = () => {
        pauseButton?.querySelector('.pause-icon--pause, .pause-icon-on')?.classList.toggle('hidden', video.paused);
        pauseButton?.querySelector('.pause-icon--play, .pause-icon-off')?.classList.toggle('hidden', !video.paused);
      };

      updateMuteIcon();
      updatePauseIcon();
      window.addEventListener('resize', updateVideoSource);

      muteButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        video.muted = !video.muted;
        updateMuteIcon();
      });

      container.addEventListener('click', (event) => {
        if (event.target.closest('.mute-icon_for_video')) return;

        if (video.paused) {
          playVideo();
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', updatePauseIcon);
      video.addEventListener('pause', updatePauseIcon);
      video.addEventListener('loadeddata', updatePauseIcon);
    });
  }

  initDossierOriginalsPromoBox() {
    const promoBox = document.querySelector('.dossier-originals_info-promo-box');
    const toggleButton = promoBox?.querySelector('.toggle-button');
    if (!promoBox || !toggleButton) return;

    toggleButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      promoBox.classList.toggle('active');
    });
  }

  // Search functionality
  triggerSearchPopup() {
    const headerSearchButton = document.querySelector('.CAT-header__search-action .search-button');
    const headerSearchPopup = document.querySelector('.header__search details');

    if (!headerSearchButton) return;

    headerSearchButton.addEventListener('click', () => {
      if (headerSearchPopup) {
        headerSearchPopup.open = true;
        const searchInput = headerSearchPopup.querySelector('input[type="search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    });
  }

  // Collection switcher menu handler
  collectionSwitcherMenuHandler() {
    if (!this.catSwitcherMenuTabs) return;

    this.catSwitcherMenuTabs.forEach((catSwitcherMenuTab, index) => {
      catSwitcherMenuTab.addEventListener('click', (event) => {
        const target = event.currentTarget;

        this.catSwitcherMenuTabs.forEach(tab => tab.classList.remove('active'));
        target.classList.add('active');

        // Bring the newly active tab into view if it's off-screen (mobile horizontal scroll)
        if (typeof autoScrollToCATNavlink === 'function') autoScrollToCATNavlink();

        // SEO - Update Collection Title
        document.title = target.dataset.collectionTitle;

        let handle = target.dataset.handle;
        this.updateBodyCollectionClass(handle);
        if (window.location.pathname.split('/').includes(handle)) return;

        const productGridId = document.querySelector('.product-grid-wrapper').id;
        this.fetchCollectionCategorie(productGridId, handle);
      });
    });
  }

  // Fetch collection categories (from CollectionHeadingManager)
  fetchCollectionCategorie(sectionId, colectionHandle) {
    let fetchUrl;
    let url;

    // Section IDs belong to their template, so fetch the destination page when entering or leaving Originals.
    const switchingCollectionTemplate = colectionHandle === 'dossier-originals'
      || this.getCurrentCollectionHandleFromPath() === 'dossier-originals';

    //fetch for products collections change
    let query_params = window.location.search.replace("?", "");

    //format query params
    if (query_params == '') {
      query_params = query_params.slice(0, query_params.indexOf('section_id=')) + query_params.slice(query_params.indexOf('&', query_params.indexOf('section_id=')) + 1);
    }
    let path = window.location.href.slice(0, window.location.href.lastIndexOf('/'));
    let new_path = window.location.pathname;

    if (switchingCollectionTemplate) {
      fetchUrl = `${window.location.origin}/collections/${colectionHandle}`;
      url = fetchUrl;

      extraFiltersSettings.closeFilter();
    } else if (new_path.split('/').length == 4) {
      let updated_path = new_path.split('/');
      updated_path[updated_path.length - 2] = colectionHandle;
      updated_path = updated_path.join('/');
      path = `${updated_path}`;

      fetchUrl = `${path}?section_id=${sectionId}&${query_params}`;
      url = `${path}${query_params != '' ? '?' : ''}${query_params}`;
    } else {
      fetchUrl = `${path}/${colectionHandle}?section_id=${sectionId}&${query_params}`;
      url = `${path}/${colectionHandle}${query_params != '' ? '?' : ''}${query_params}`;
    }

    // Update url
    window.history.pushState("", "", url);

    // Add loading to container
    const collectionElement = document.querySelector('.collection');

    if(collectionElement) {
      collectionElement.classList.add('loading');
    }

    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load collection ${colectionHandle}: ${response.status}`);
        }
        return response.text();
      })
      .then(async (response) => {
        const section_id = `#${sectionId}`;

        this.renderNewItems(section_id, response, switchingCollectionTemplate);

        // Special case for vanilla expression collection
        if (colectionHandle == 'vanilla-expressions') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(response, 'text/html');
          const allScripts = doc.querySelectorAll('[data-script-to-reload]');
          for (const oldScript of allScripts) {
            const runnableScript = document.createElement('script');
            if (oldScript.hasAttribute('src')) {
              runnableScript.src = oldScript.getAttribute('src');
              await new Promise((resolve, reject) => {
                runnableScript.onload = resolve;
                runnableScript.onerror = reject;
                document.body.appendChild(runnableScript);
              });
            } else {
              runnableScript.textContent = oldScript.textContent;
              document.body.appendChild(runnableScript);
              runnableScript.remove();
            }
          }
          if (typeof window.initDossierSpecialCarousel === 'function' && typeof window.initDossierParallaxSection === 'function') {
            window.initDossierSpecialCarousel();
            window.initDossierParallaxSection();
          } else {
            console.error('Could not access the carousel function.');
            console.log('redirect')
            setTimeout(() => {
              window.location.href = `${Shopify.routes.root}collections/vanilla-expressions`
            }, 500)
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        collectionElement?.classList.remove('loading');
      });
  }

  // Render new items (from CollectionHeadingManager)
  renderNewItems(section, html, switchingCollectionTemplate) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');

    const newEmptyResults = parsedHtml.querySelector('.no-results');

    // A fetched full page uses a different template, so its wrapper id won't match ours - use the stable class instead
    const newSection = switchingCollectionTemplate ? parsedHtml.querySelector('.product-grid-wrapper') : parsedHtml.querySelector(section);
    const currentSection = switchingCollectionTemplate ? document.querySelector('.product-grid-wrapper') : document.querySelector(section);

    if (!newSection || !currentSection) {
      throw new Error(`Collection content not found for ${window.location.pathname}`);
    }

    if (newSection && currentSection) {
      if (switchingCollectionTemplate) {
        // Swap the wrapper in both directions so the destination template's section ID is preserved.
        const currentWrapperParent = currentSection.parentElement;
        const newWrapperParent = newSection.parentElement;

        if (currentWrapperParent && newWrapperParent) {
          currentWrapperParent.replaceWith(newWrapperParent.cloneNode(true));
        } else {
          currentSection.innerHTML = newSection.innerHTML;
        }
      } else {
        currentSection.innerHTML = newSection.innerHTML;
      }

      // Reset pagination state for the (possibly freshly swapped-in) product grid, whatever the transition
      const newProductGrid = parsedHtml.querySelector('#product-grid');
      const currentProductGrid = document.querySelector('#product-grid');

      if (newProductGrid && currentProductGrid) {
        currentProductGrid.classList.add('pageOne');
        currentProductGrid.dataset.pages = newProductGrid.dataset.pages;
        this.CAT_collection_grid_template_id = currentProductGrid.dataset.id;
      }

      this.initDossierOriginals();
      this.initPromoCardsVideoLogic();

      // Add Filters Functionality
      extraFiltersSettings.init();

      if (newEmptyResults) return;

      setTimeout(() => {
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(newSection);
        if (typeof initializeScrollZoomAnimationTrigger === 'function') initializeScrollZoomAnimationTrigger(newSection);
      }, 10);

      showDynamicPrices();

      // Handle sticky filter button
      this.handleStickyFilterButton();

      // Open search popup
      this.triggerSearchPopup();
    }
  }

  // Infinite scroll initialization
  initInfiniteScroll() {
    document.addEventListener("scroll", () => {
      this.infiniteScrollRender();
    });
  }

  // Setup initial filters and UTM products
  setupInitialFilters() {
    setTimeout(() => {
      this.utmProductsFilter();
    }, 200);
  }

  // Section Rendering URL for one page of the collection grid
  getCatPageUrl(sectionId, page_nr) {
    let query_params = window.location.search.replace("?", "");
    return `${window.location.pathname}?section_id=${sectionId}&${query_params}&page=${page_nr}`;
  }

  // Fetch new category items
  async fetchNewCatItems(sectionId, page_nr, target_element) {
    this.isFetching = true;
    let response = await fetch(this.getCatPageUrl(sectionId, page_nr));
    let results = await response.text();
    this.isFetching = false;

    this.renderNewCatItems(target_element, results);
  }

  // Render new category items
  renderNewCatItems(target, html) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
  
    if (parsedHtml.querySelector('.no-results')) return;
  
    parsedHtml.querySelectorAll('.promo-card').forEach(el => el.remove());
  
    const container = document.querySelector(target);
    const newContainer = parsedHtml.querySelector(target);
  
    if (!container || !newContainer) return;
  
    const newItems = Array.from(newContainer.children);
    container.append(...newItems);

    // Reveal the newly appended items right away instead of letting the
    // intersection observer re-hide below-the-fold items and delay them.
    newItems.forEach((item) => {
      item.classList.remove('scroll-trigger--offscreen');
      item.querySelectorAll('.scroll-trigger--offscreen').forEach((el) => el.classList.remove('scroll-trigger--offscreen'));
    });

    requestAnimationFrame(() => {
      if (typeof initializeScrollZoomAnimationTrigger === 'function') {
        initializeScrollZoomAnimationTrigger();
      }
    });
  
    if (this.UTMproductsRearenged) {
      this.removeDuplicateProducts();
    }
  }

  // Infinite scroll loads the pinned UTM products again on their own page: keep only the pinned copy
  removeDuplicateProducts() {
    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;

    this.getUTMProductIds().forEach((productId) => {
      const items = Array.from(productGrid.children).filter((item) => this.isGridItemForProduct(item, productId));
      items.slice(1).forEach((item) => item.remove());
    });
  }

  // Handle infinite scroll rendering
  async infiniteScrollRender() {
    // Wait until the UTM products are pinned, so pages don't load twice and pagination stays in order
    if (document.documentElement.classList.contains('utm-products-pending')) return;

    let productGrid = document.querySelector('#product-grid');
    let currentPagesNr = productGrid.dataset.pages;

    if (productGrid.classList.contains('pageOne')) {
      this.pageNr = 1;
      productGrid.classList.remove('pageOne');
    }

    let viewportHeight = window.innerHeight;
    let scrollY = window.scrollY;

    let productItems = productGrid.querySelectorAll('.grid__item');
    let lastItem = productItems[productItems.length - 1];

    if (!lastItem) return;

    const rect = lastItem.getBoundingClientRect();
    const lastItemPositionInViewport = rect.top + scrollY;

    // Start fetching the next page before the shopper actually reaches the last
    // product, so the new items are usually ready by the time they get there and
    // the grid feels seamless instead of pausing at the bottom. Prefetching ~1.5
    // viewports early hides the network round-trip.
    const preloadOffset = viewportHeight * 1.5;

    if (scrollY + viewportHeight + preloadOffset >= lastItemPositionInViewport) {
      if (this.pageNr < currentPagesNr && this.isFetching == false) {
        this.pageNr++;
        await this.fetchNewCatItems(this.CAT_collection_grid_template_id, this.pageNr, '#product-grid');
        showDynamicPrices();
      }
    }

    this.offScreenAnimationFix();
  }

  // UTM products: ?utm_products=<handle>_<handle> (ads, emails) pins those products to the top of the grid, in that order
  getUTMProductIds() {
    const utmProductsValue = new URLSearchParams(window.location.search).get('utm_products');
    if (!utmProductsValue) return [];

    return [...new Set(utmProductsValue.split('_').map((productId) => productId.trim()).filter(Boolean))];
  }

  // Grid items are <li class="grid__item"><div id="{handle}" data-product-id="{product id}">: links can use either.
  // Compared as strings rather than with a #id selector, which is invalid for values starting with a digit
  isGridItemForProduct(item, productId) {
    const card = item.children[0];
    return !!card && (card.id === productId || card.dataset.productId === productId);
  }

  findGridItemByProductId(productGrid, productId) {
    return Array.from(productGrid.children).find((item) => this.isGridItemForProduct(item, productId));
  }

  // The utm-products-pending snippet hides the grid until the pinned products are in place,
  // so moving cards never shows up as a layout shift (CLS)
  async utmProductsFilter() {
    const root = document.documentElement;
    const productGrid = document.querySelector('#product-grid');
    const productIds = this.getUTMProductIds();

    try {
      if (!productGrid || !productIds.length) return;

      const pinnedItems = new Map();
      productIds.forEach((productId) => {
        const item = this.findGridItemByProductId(productGrid, productId);
        if (item) pinnedItems.set(productId, item);
      });

      // Products that aren't on the first page: load the other pages in parallel and stop once all are found
      const pagesCount = parseInt(productGrid.dataset.pages, 10) || 1;
      if (pinnedItems.size < productIds.length && pagesCount > 1) {
        const controller = new AbortController();
        const pageNumbers = Array.from({ length: pagesCount - 1 }, (_, index) => index + 2);

        await Promise.all(pageNumbers.map(async (pageNr) => {
          try {
            const response = await fetch(this.getCatPageUrl(this.CAT_collection_grid_template_id, pageNr), { signal: controller.signal });
            const pageGrid = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('#product-grid');
            if (!pageGrid) return;

            productIds.forEach((productId) => {
              if (pinnedItems.has(productId)) return;
              const item = this.findGridItemByProductId(pageGrid, productId);
              if (item) pinnedItems.set(productId, item);
            });

            if (pinnedItems.size === productIds.length) controller.abort();
          } catch (error) {
            if (error.name !== 'AbortError') console.error(error);
          }
        }));
      }

      // The snippet's fallback timer already showed the grid: moving cards now would shift the page
      if (!root.classList.contains('utm-products-pending')) return;

      [...productIds].reverse().forEach((productId) => {
        const item = pinnedItems.get(productId);
        if (item) productGrid.insertBefore(item, productGrid.firstElementChild);
      });

      this.UTMproductsRearenged = true;
      showDynamicPrices();
    } catch (error) {
      console.error(error);
    } finally {
      root.classList.remove('utm-products-pending');
    }
  }

  // Fix off-screen animations
  offScreenAnimationFix() {
    let container = document.querySelector("#product-grid.grid--custom");
    if (!container) return;

    const isElementInViewport = (element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      return (
        rect.top < viewportHeight &&
        rect.bottom > 0 &&
        rect.left < viewportWidth &&
        rect.right > 0
      );
    };

    container.querySelectorAll('.scroll-trigger--offscreen').forEach(el => {
      if (isElementInViewport(el)) {
        el.classList.remove('scroll-trigger--offscreen');
      }
    });
  }

  handleStickyFilterButton() {
    const button = document.querySelector(".CAT-header__filters-action");
    if (!button) return;

    const parent = button.parentElement;
    const headerHeight = 111; // px
    const buttonOffsetTop = button.getBoundingClientRect().top + window.scrollY;

    // Function to store the current width in a data attribute
    const updateParentWidth = () => {
      parent.style.setProperty("--sticky-width", `${button.offsetWidth}px`);
    };

    // Set initial width
    updateParentWidth();

    // Observe width changes with ResizeObserver
    const resizeObserver = new ResizeObserver(updateParentWidth);
    resizeObserver.observe(button);

    // Optional: also update width on button click if toggle changes size
    button.addEventListener("click", () => {
      setTimeout(updateParentWidth, 50); // slight delay to allow DOM changes
    });

    // Scroll handling for sticky class
    window.addEventListener("scroll", () => {
      if (window.innerWidth > 990) {
        if (window.scrollY > buttonOffsetTop - headerHeight) {
          parent.classList.add("filters-sticky");
        } else {
          parent.classList.remove("filters-sticky");
        }
      }
    });
  }
}

const collectionGridManager = new CollectionGridManager();